import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#26C281", "#0EA5E9", "#F59E0B", "#A855F7", "#EF4444", "#10B981", "#6366F1"];

const Earnings = () => {
    const [trend, setTrend] = useState([]);
    const [split, setSplit] = useState([]);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/dashboard/revenue-trend?days=30"),
            api.get("/dashboard/vehicle-split"),
            api.get("/dashboard/stats"),
        ]).then(([t, v, s]) => { setTrend(t.data); setSplit(v.data); setStats(s.data); });
    }, []);

    if (!stats) return <div className="text-slate-500">Loading…</div>;

    const trendData = trend.map((d) => ({
        date: new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        revenue: d.revenue,
        commission: d.commission,
        rides: d.rides,
    }));

    const driverPayout = (stats.total_revenue || 0) - (stats.total_commission || 0);

    return (
        <div className="space-y-6">
            <PageHeader title="Earnings & Analytics" subtitle="Revenue, commission and driver payouts" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-mint-500 to-mint-600 text-white rounded-2xl p-6">
                    <div className="text-xs uppercase tracking-wider text-mint-100 font-semibold">Total Revenue</div>
                    <div className="font-display font-bold text-3xl mt-2">{formatCurrency(stats.total_revenue)}</div>
                    <div className="text-sm text-mint-100 mt-2">{stats.completed_rides} completed rides</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Platform Commission</div>
                    <div className="font-display font-bold text-3xl mt-2 text-navy-900">{formatCurrency(stats.total_commission)}</div>
                    <div className="text-sm text-slate-500 mt-2">per-rule %</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Driver Payouts</div>
                    <div className="font-display font-bold text-3xl mt-2 text-navy-900">{formatCurrency(driverPayout)}</div>
                    <div className="text-sm text-slate-500 mt-2">net of commission</div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="font-display font-semibold text-navy-900 mb-1">Revenue & commission trend</div>
                <div className="text-xs text-slate-500 mb-4">Last 30 days</div>
                <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                        <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="revenue" stroke="#26C281" strokeWidth={2.5} dot={false} />
                            <Line type="monotone" dataKey="commission" stroke="#0EA5E9" strokeWidth={2.5} dot={false} />
                            <Line type="monotone" dataKey="rides" stroke="#F59E0B" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="font-display font-semibold text-navy-900 mb-4">Revenue by vehicle type</div>
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <BarChart data={split}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="vehicle_type" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={60} />
                                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Bar dataKey="revenue" fill="#26C281" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="font-display font-semibold text-navy-900 mb-4">Ride share by vehicle</div>
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={split}
                                    dataKey="count"
                                    nameKey="vehicle_type"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    innerRadius={50}
                                    paddingAngle={2}
                                    label={(e) => `${e.vehicle_type}`}
                                    labelLine={false}
                                    style={{ fontSize: 11 }}
                                >
                                    {split.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Earnings;
